// TODO: Remove this ignore when the code is fixed
// It was easier to conver utils.ts to .ts files than to fix the type errors in the existing .tsx files.

import clsx, { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import { Subscription } from 'src/types/subscription';
import { Fish } from '@Types/fish';

/**
 * Ensures a phone number has the +1 prefix for backend communication
 * @param {string} phone - Phone number with or without +1 prefix
 * @returns {string} Phone number with +1 prefix
 */
const ensurePhonePrefix = (phone: string) => {
  if (!phone) return null;
  let formattedPhone;
  const digitsOnly = phone.replace(/\D/g, '').trim();

  if (digitsOnly.length === 10) {
    formattedPhone = '+1' + digitsOnly;
  } else if (digitsOnly.length === 11) {
    formattedPhone = '+' + digitsOnly;
  }
  return formattedPhone || null;
};

/**
 * Removes +1 prefix from phone number for display purposes
 * @param {string} phone - Phone number with or without +1 prefix
 * @returns {string} Phone number without +1 prefix
 */
const displayPhone = (phone: string) => phone?.replace(/^\+1/, '');

const toLocalDateTime = (utcDate: string) => {
  const localDate = new Date(utcDate);
  const offset = localDate.getTimezoneOffset();
  localDate.setMinutes(localDate.getMinutes() - offset);

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Recursively applies `transformFn` to a node with the matching `targetId`.
 * If `transformFn` returns `null`, that node is removed (deleted).
 *
 * After processing children:
 * - If an AND block becomes empty, we replace it with a single blank condition.
 * - If an OR block becomes empty, we also replace it with a single blank condition.
 */
function transformConditionById(
  node: any,
  targetId: string,
  transformFn: (node: any) => any | null,
): any {
  if (!node) return node;

  const isGroup = node.type && Array.isArray(node.conditions);

  // 1) SINGLE (LEAF) CONDITION
  if (!isGroup) {
    if (node.id === targetId) {
      // If transformFn returns null => remove leaf
      return transformFn(node);
    }
    // Not the target => unchanged
    return node;
  }

  // 2) GROUP WITH MATCHING ID
  if (node.id === targetId) {
    const transformed = transformFn(node);
    if (transformed === null) {
      // Entire group removed
      return null;
    }

    // If transformFn changed something else, we still want to transform children
    // in case we also remove nested nodes within.
    // So re-run on children (unless transformFn returned a leaf).
    if (transformed.type && Array.isArray(transformed.conditions)) {
      const updatedChildren = transformed.conditions
        .map((child: any) =>
          transformConditionById(child, targetId, transformFn),
        )
        .filter((child: any) => child !== null);

      let updatedGroup = { ...transformed, conditions: updatedChildren };

      // Post-process for empty AND or OR
      if (updatedGroup.type === 'AND' && updatedGroup.conditions.length === 0) {
        updatedGroup = makeBlankCondition();
      } else if (
        updatedGroup.type === 'OR' &&
        updatedGroup.conditions.length === 0
      ) {
        updatedGroup = makeBlankCondition();
      } else if (updatedGroup.conditions.length === 1) {
        // If group has exactly one child, return just that child
        return updatedGroup.conditions[0];
      }

      return updatedGroup;
    }
    // If transformFn turned the group into a single condition object, just return it
    return transformed;
  }

  // 3) GROUP WITH NON-MATCHING ID => transform children
  const updatedChildren = node.conditions
    .map((child: any) => transformConditionById(child, targetId, transformFn))
    .filter((child: any) => child !== null);

  let updatedGroup = { ...node, conditions: updatedChildren };

  // Post-process empty AND or OR
  if (updatedGroup.type === 'AND' && updatedGroup.conditions.length === 0) {
    updatedGroup = null;
  } else if (
    updatedGroup.type === 'OR' &&
    updatedGroup.conditions.length === 0
  ) {
    updatedGroup = makeBlankCondition();
  } else if (updatedGroup.conditions.length === 1) {
    // If group has exactly one child, return just that child
    return updatedGroup.conditions[0];
  }

  return updatedGroup;
}

function makeBlankCondition() {
  return {
    id: uuidv4(),
    field: 'Tags',
    operator: 'isTagged',
    value: '',
  };
}

const orderFlowStages = (stages: any[] = []) => {
  const result: any[] = [];
  const finalStage = stages.find((stage) => stage.nextStageId === null);

  if (finalStage) {
    result.push(finalStage);

    for (let i = 0; i < stages.length; i++) {
      const previousStage = stages.find(
        (stage) =>
          stage.nextStageId ===
          (result[result.length - 1].flowStageId
            ? result[result.length - 1].flowStageId
            : result[result.length - 1].id),
      );
      if (previousStage) {
        result.push(previousStage);
      }
    }
    result.reverse();
  }
  return result;
};

const formatPhone = (phoneNum: string) => {
  if (phoneNum.length === 10) {
    return phoneNum.replace(/(\d{3})(\d{3})(\d{4})/, '($1)-$2-$3');
  } else if (phoneNum.length === 12) {
    return phoneNum.slice(2).replace(/(\d{3})(\d{3})(\d{4})/, '($1)-$2-$3');
  }
};

export {
  toLocalDateTime,
  transformConditionById,
  makeBlankCondition,
  ensurePhonePrefix,
  displayPhone,
  orderFlowStages,
  formatPhone,
};

/**
 * Returns true if:
 * - activePath exactly equals comparePath, OR
 * - activePath starts with comparePath + "/"  (for wildcard-style matching)
 */
export function isActivePath(
  activePathname: string,
  comparePathname: string,
): boolean {
  const normalize = (path: string) => {
    // Strip query/hash
    const [clean] = path.split(/[?#]/);

    if (!clean) return '/';

    // Remove trailing slash except for root "/"
    if (clean.length > 1 && clean.endsWith('/')) {
      return clean.slice(0, -1);
    }

    return clean;
  };

  const active = normalize(activePathname);
  const compare = normalize(comparePathname);

  if (active === compare) return true;

  // wildcard: `/parent/...` should match `/parent`
  return active.startsWith(compare + '/');
}

export const GREEN = 'oklch(64.8% 0.2 131.684)';
export const RED = 'oklch(50.5% 0.213 27.518)';
export const ORANGE = 'oklch(66.6% 0.179 58.318)';
export function getDisplayMemberSubscriptionStatus(
  subscriptions: Array<Subscription>,
) {
  const memberSubscription = subscriptions.find(
    (subscription) => subscription.subscriptionType === 'STORK_CLUB_MEMBERSHIP',
  );
  let displayStatus: string = 'Active';
  let displayColor: string = GREEN;
  if (memberSubscription?.cancelledAt) {
    const nextSubscriptionRenewal = DateTime.fromISO(
      memberSubscription.nextSubscriptionRenewal,
    );
    const isExpired = nextSubscriptionRenewal < DateTime.now();
    displayStatus = isExpired ? 'Cancel Expired' : 'Cancel Pending';
    displayColor = isExpired ? RED : ORANGE;
  }
  return {
    status: displayStatus,
    color: displayColor,
  };
}

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(...inputs));
};

export const hasTag = (tag: string, fish: Fish) => {
  return (
    fish.tags &&
    fish.tags.some((t) => t.tag.name.toUpperCase() === tag.toUpperCase())
  );
};
